import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { PENDING, IDLE } from "const"
import { MtProjectGroup } from "server/models"
import { deepCopy, StateType } from "client/utils"
import {
    StoreType,
    ProjectUserGroupProps,
    ProjectGroupFilters,
    ProjectUserGroupList,

    LoadProjectGroups,
    LoadProjectUserGroups,
    DeleteProjectUserGroup,
    SortProjectGroups,

    setProjectGroupList,
    setProjectUserGroupList,
    setAssignedProjectUserGroupIds,
    setAssignedProjectUserGroupSort,
    setAssignedProjectUserGroupFilters,
    setNotAssignedProjectUserGroupSelected,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    Button,
    TableSearchWrapper,
    Table,
    SortRows,
    DeleteRow,
} from "client/components"

import ProjectGroupSearch from "client/pages/search/projectGroupSearch"
import Columns from "./columns"
import NotAssignedGroups from "../notAssigned"

const AssignedGroups = ({
    user,
}: ProjectUserGroupProps) => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const groupList = useSelector((state: StoreType) => state.projectGroup.groupList)

    const userGroupList = useSelector((state: StoreType) => state.projectUserGroup.userGroupList)
    const userGroupIds = (userGroupList && userGroupList[user.id]) || []

    const groupIds = useSelector((state: StoreType) => state.projectUserGroup.assignedGroups.groupIds)
    const sortColumn = useSelector((state: StoreType) => state.projectUserGroup.assignedGroups.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.projectUserGroup.assignedGroups.sortOrder)
    const filters = useSelector((state: StoreType) => state.projectUserGroup.assignedGroups.filters)
    const reloadList = useSelector((state: StoreType) => state.projectUserGroup.reloadUserGroupList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        Promise.all([
            LoadProjectGroups(project.accountId, project.id, true)
            .then((res) => {
                dispatch(setProjectGroupList(res))
            }),
            LoadProjectUserGroups(user.id, project.accountId, project.id, groupList)
            .then((res) => {
                const userGroupListCopy: ProjectUserGroupList = userGroupList ? deepCopy(userGroupList) : {}
                userGroupListCopy[user.id] = res
                dispatch(setProjectUserGroupList(userGroupListCopy))
            }),
        ]).then(() => {
            setLoadState(IDLE)
        })
    }, [reloadList])

    useEffect(() => {
        const sortedGroupIds = SortProjectGroups(Object.values(groupList), sortColumn, sortOrder, filters)
        const sortedAssigned = sortedGroupIds.filter((groupId) => userGroupIds.includes(groupId))
        dispatch(setAssignedProjectUserGroupIds(sortedAssigned))
    }, [userGroupList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof MtProjectGroup, order) => {
        dispatch(setAssignedProjectUserGroupSort(column, order))
    }

    const onFilter = (newFilters: ProjectGroupFilters) => {
        dispatch(setAssignedProjectUserGroupFilters(newFilters))
    }

    const onDelete: DeleteRow = (group: MtProjectGroup, callback) => {
        DeleteProjectUserGroup(user.id, group.groupId, project.id, project.accountId).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const userGroupListCopy: ProjectUserGroupList = userGroupList ? deepCopy(userGroupList) : {}
                userGroupListCopy[user.id] = userGroupIds.filter((id) => id !== group.groupId)
                dispatch(setProjectUserGroupList(userGroupListCopy))
            })
        })
    }

    const columns = Columns()

    return (
        <Container>
            <PageHeader>
                <PageTitle>{`${user.firstName} ${user.lastName} ${lang.groups}`}</PageTitle>
            </PageHeader>
            <PageContent>
                {loadState === PENDING && <Loader />}
                <TableSearchWrapper>
                    <ProjectGroupSearch
                        id="assignedProjectUserGroups"
                        rightOffset
                        filters={filters}
                        onFilter={onFilter}
                    />
                    <Button
                        variant="success"
                        onClick={() => {
                            dispatch(setNotAssignedProjectUserGroupSelected(null))
                            dispatch(addModal({
                                key: "notAssignedGroups",
                                content: <NotAssignedGroups user={user} />,
                            }))
                        }}
                    >
                        {lang.add}
                    </Button>
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={groupList}
                    dataIds={groupIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="sm"
                    showPagination
                    onSort={onSort}
                    onDelete={onDelete}
                />
            </PageContent>
        </Container>
    )
}
export default AssignedGroups
